import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const Shell = ({ children, tone }) => (
  <div className="grid min-h-screen place-items-center bg-brand-cream px-5 py-10 grain">
    <div className={`w-full max-w-md rounded-3xl border-2 border-brand-ink p-8 text-center shadow-brutal-lg ${tone}`} data-testid="verify-card">
      <Link to="/" className="mb-4 inline-flex items-center gap-2 font-display text-xl font-medium lowercase">
        <img src="/panda-mascot.png" alt="" className="h-8 w-8 object-contain" /> panda tata
      </Link>
      {children}
    </div>
  </div>
);

export default function VoucherVerify() {
  const { code } = useParams();
  const [state, setState] = useState({ loading: true });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setState({ loading: true });
    try {
      const { data } = await api.get(`/vouchers/verify/${code}`);
      setState({ loading: false, ...data });
    } catch (e) {
      setState({ loading: false, found: false });
    }
  }, [code]);

  useEffect(() => { load(); }, [load]);

  const markUsed = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/vouchers/verify/${code}/use`);
      if (data.ok) toast.success("Marked as used");
      else toast.info("Already used");
      load();
    } catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  if (state.loading) return <Shell tone="bg-white"><Loader2 className="mx-auto h-12 w-12 animate-spin" /></Shell>;

  if (!state.found)
    return (
      <Shell tone="bg-white">
        <XCircle className="mx-auto h-16 w-16 text-red-500" />
        <h1 className="mt-4 font-display text-3xl font-bold" data-testid="verify-status">Not Found</h1>
        <p className="mt-2 text-muted-foreground">No voucher matches code <span className="font-mono font-bold">{code}</span>.</p>
      </Shell>
    );

  const used = state.status === "used";
  const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

  return (
    <Shell tone={used ? "bg-muted" : "bg-brand-green"}>
      {used ? <AlertTriangle className="mx-auto h-16 w-16" /> : <CheckCircle2 className="mx-auto h-16 w-16" />}
      <h1 className="mt-4 font-display text-4xl font-black uppercase" data-testid="verify-status">{used ? "Used" : "Valid"}</h1>
      <div className="mt-6 space-y-1 rounded-2xl border-2 border-brand-ink bg-white p-5 text-left text-sm">
        <p className="font-display text-2xl font-bold">{state.title}</p>
        <p><span className="font-semibold">Member:</span> {state.member_name || "—"}</p>
        <p><span className="font-semibold">Code:</span> <span className="font-mono tracking-widest">{code.toUpperCase()}</span></p>
        <p><span className="font-semibold">Points:</span> {state.points_cost}</p>
        <p><span className="font-semibold">Redeemed:</span> {fmt(state.redeemed_at)}</p>
        {used && <p><span className="font-semibold">Used:</span> {fmt(state.used_at)}</p>}
      </div>
      {!used && (
        <button onClick={markUsed} disabled={busy} data-testid="verify-mark-used"
          className="mt-6 w-full rounded-full border-2 border-brand-ink bg-brand-ink py-3 font-bold text-brand-yellow hover-brutal disabled:opacity-60">
          {busy ? "..." : "Mark as Used"}
        </button>
      )}
      <p className="mt-4 text-xs text-brand-ink/60">Outlet staff verification</p>
    </Shell>
  );
}
